import { FirestoreErrorCode } from 'firebase/firestore';
import { Platform } from 'react-native';

import { ErrorProps } from '@components/ErrorView';
import { logError } from '@helpers/Loggers';

export type AppErrorKind =
  | 'validation'
  | 'offline'
  | 'timeout'
  | 'unauthorized'
  | 'forbidden'
  | 'not-found'
  | 'conflict'
  | 'navigation'
  | 'server'
  | 'unknown';

export type AppErrorOptions = {
  code?: string;
  cause?: unknown;
  fieldErrors?: Record<string, string>;
  retryable?: boolean;
  status?: number;
};

export class AppError extends Error {
  readonly code?: string;
  readonly fieldErrors?: Record<string, string>;
  readonly kind: AppErrorKind;
  readonly retryable: boolean;
  readonly status?: number;

  constructor(message: string, kind: AppErrorKind, options: AppErrorOptions = {}) {
    super(message);
    this.name = this.constructor.name;
    this.code = options.code;
    this.fieldErrors = options.fieldErrors;
    this.kind = kind;
    this.retryable = options.retryable ?? isRetryableErrorKind(kind);
    this.status = options.status;
    if (options.cause !== undefined) {
      Object.assign(this, { cause: options.cause });
    }
  }
}

export function createNotFoundAppError(cause?: unknown) {
  return new AppError(getUserErrorMessageForKind('not-found'), 'not-found', { cause });
}

export function createNavigationAppError(cause?: unknown) {
  return new AppError(getUserErrorMessageForKind('navigation'), 'navigation', { cause });
}

type ErrorHandler = (error: unknown, isFatal?: boolean) => void;

let reactNativeDefaultGlobalHandler: ErrorHandler | undefined;
let appGlobalHandlerInstalled = false;
let webGlobalHandlersInstalled = false;

export function setExceptionHandlers() {
  if (Platform.OS === 'web') {
    if (webGlobalHandlersInstalled || typeof globalThis.addEventListener !== 'function') return;

    webGlobalHandlersInstalled = true;
    globalThis.addEventListener('error', (event) => {
      const errorEvent = event as ErrorEvent;
      logError(errorEvent.error ?? errorEvent.message);
    });
    globalThis.addEventListener('unhandledrejection', (event) => {
      const rejectionEvent = event as PromiseRejectionEvent;
      logError(rejectionEvent.reason);
    });
    return;
  }

  const previousHandler = appGlobalHandlerInstalled
    ? reactNativeDefaultGlobalHandler
    : ErrorUtils.getGlobalHandler();
  reactNativeDefaultGlobalHandler = previousHandler;
  appGlobalHandlerInstalled = true;

  ErrorUtils.setGlobalHandler((error, isFatal) => {
    logError(error, { isFatal });
    previousHandler?.(error, isFatal);
  });
}

export function toAppError(error: unknown): AppError {
  if (error instanceof AppError) return error;

  const code = getErrorCode(error);
  const status = getErrorStatus(error);
  const kind = getAppErrorKind(error, code, status);
  return new AppError(getUserErrorMessageForKind(kind), kind, {
    cause: error,
    code,
    fieldErrors: getFieldErrors(error),
    retryable: isRetryableAppError(code, kind, status),
    status,
  });
}

export function getUserErrorMessage(error: unknown) {
  const appError = toAppError(error);
  return appError.message || getUserErrorMessageForKind(appError.kind);
}

export function shouldReportError(error: unknown) {
  const kind = toAppError(error).kind;
  return kind === 'server' || kind === 'unknown';
}

export function isPermissionDeniedError(error: unknown) {
  return toAppError(error).kind === 'forbidden';
}

export function isNotFoundError(error: unknown) {
  return toAppError(error).kind === 'not-found';
}

export function isRetryableError(error: unknown) {
  return toAppError(error).retryable;
}

export type GetErrorPropsOptions = {
  /** Retries the failed operation in place when the error screen can recover locally. */
  onRetry?: () => Promise<unknown> | unknown;
};

export const getErrorProps = (error: unknown, options: GetErrorPropsOptions = {}): ErrorProps => {
  const appError = toAppError(error);
  if (appError.kind === 'forbidden') {
    return {
      tapToReload: false,
      message: getUserErrorMessage(appError),
    };
  } else if (appError.kind === 'not-found') {
    return {
      tapToReload: false,
      message: getUserErrorMessage(appError),
    };
  } else if (appError.kind === 'navigation') {
    return {
      tapToReload: false,
      message: getUserErrorMessage(appError),
    };
  } else {
    return {
      message: getUserErrorMessage(appError),
      onRetry: options.onRetry,
      tapToReload: true,
    };
  }
};

function getAppErrorKind(
  error: unknown,
  code: string | undefined,
  status: number | undefined
): AppErrorKind {
  if (status) {
    if (status === 400 || status === 422) return 'validation';
    if (status === 401) return 'unauthorized';
    if (status === 403) return 'forbidden';
    if (status === 404) return 'not-found';
    if (status === 408) return 'timeout';
    if (status === 409) return 'conflict';
    if (status === 429 || status >= 500) return 'server';
  }

  if (!code) {
    return isLikelyNetworkError(error) ? 'offline' : 'unknown';
  }

  if (VALIDATION_ERROR_CODES.has(code)) return 'validation';
  if (OFFLINE_ERROR_CODES.has(code)) return 'offline';
  if (TIMEOUT_ERROR_CODES.has(code)) return 'timeout';
  if (UNAUTHORIZED_ERROR_CODES.has(code)) return 'unauthorized';
  if (FORBIDDEN_ERROR_CODES.has(code)) return 'forbidden';
  if (NOT_FOUND_ERROR_CODES.has(code)) return 'not-found';
  if (CONFLICT_ERROR_CODES.has(code)) return 'conflict';
  if (SERVER_ERROR_CODES.has(code)) return 'server';

  return 'unknown';
}

function getUserErrorMessageForKind(kind: AppErrorKind) {
  switch (kind) {
    case 'validation':
      return 'Please check the highlighted fields and try again.';
    case 'offline':
      return 'You appear to be offline. Please check your connection and try again.';
    case 'timeout':
      return 'The request took too long. Please try again.';
    case 'unauthorized':
      return 'Please sign in again and retry.';
    case 'forbidden':
      return "You don't have permission to view this content.";
    case 'not-found':
      return 'The content you are trying to view does not exist.';
    case 'conflict':
      return 'This change conflicts with the current state. Please refresh and try again.';
    case 'navigation':
      return "We couldn't take you there. We've been notified and are looking into it.";
    case 'server':
      return 'The service is temporarily unavailable. Please try again.';
    case 'unknown':
      return 'Something went wrong. Please try again.';
  }
}

function getErrorCode(error: unknown) {
  const code = getStringProperty(error, 'code');
  if (code) return code;

  const response = getRecordProperty(error, 'response');
  return getStringProperty(response, 'code');
}

function getErrorStatus(error: unknown) {
  const status = getNumberProperty(error, 'status');
  if (status) return status;

  const response = getRecordProperty(error, 'response');
  return getNumberProperty(response, 'status');
}

function getFieldErrors(error: unknown) {
  const fieldErrors = getRecordProperty(error, 'fieldErrors');
  if (fieldErrors && hasOnlyStringValues(fieldErrors)) return fieldErrors;

  const options = getRecordProperty(error, 'options');
  const optionFieldErrors = getRecordProperty(options, 'fieldErrors');
  if (optionFieldErrors && hasOnlyStringValues(optionFieldErrors)) return optionFieldErrors;

  return undefined;
}

function getRecordProperty(value: unknown, key: string): Record<string, unknown> | undefined {
  if (!isRecord(value)) return undefined;

  const property = value[key];
  return isRecord(property) ? property : undefined;
}

function getStringProperty(value: unknown, key: string): string | undefined {
  if (!isRecord(value)) return undefined;

  const property = value[key];
  return typeof property === 'string' ? property : undefined;
}

function getNumberProperty(value: unknown, key: string): number | undefined {
  if (!isRecord(value)) return undefined;

  const property = value[key];
  return typeof property === 'number' ? property : undefined;
}

function hasOnlyStringValues(value: Record<string, unknown>): value is Record<string, string> {
  return Object.values(value).every((fieldError) => typeof fieldError === 'string');
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isLikelyNetworkError(error: unknown) {
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  return /failed to fetch|network request failed|networkerror|offline|internet connection/i.test(
    message
  );
}

function isRetryableAppError(
  code: string | undefined,
  kind: AppErrorKind,
  status: number | undefined
) {
  if (kind === 'offline' || kind === 'timeout' || kind === 'server') return true;
  if (status === 429 || (status !== undefined && status >= 500)) return true;

  return code ? RETRYABLE_ERROR_CODES.has(code) : false;
}

function isRetryableErrorKind(kind: AppErrorKind) {
  return kind === 'offline' || kind === 'timeout' || kind === 'server';
}

const validationCode: FirestoreErrorCode = 'invalid-argument';
const permissionDeniedCode: FirestoreErrorCode = 'permission-denied';
const notFoundCode: FirestoreErrorCode = 'not-found';
const unavailableCode: FirestoreErrorCode = 'unavailable';
const deadlineExceededCode: FirestoreErrorCode = 'deadline-exceeded';
const unauthenticatedCode: FirestoreErrorCode = 'unauthenticated';
const alreadyExistsCode: FirestoreErrorCode = 'already-exists';
const resourceExhaustedCode: FirestoreErrorCode = 'resource-exhausted';
const abortedCode: FirestoreErrorCode = 'aborted';
const internalCode: FirestoreErrorCode = 'internal';

const VALIDATION_ERROR_CODES = new Set<string>([
  validationCode,
  'auth/invalid-email',
  'auth/invalid-credential',
  'auth/wrong-password',
  'auth/weak-password',
]);
const OFFLINE_ERROR_CODES = new Set<string>([unavailableCode, 'auth/network-request-failed']);
const TIMEOUT_ERROR_CODES = new Set<string>([deadlineExceededCode, 'ECONNABORTED', 'ETIMEDOUT']);
const UNAUTHORIZED_ERROR_CODES = new Set<string>([unauthenticatedCode, 'auth/user-token-expired']);
const FORBIDDEN_ERROR_CODES = new Set<string>([
  permissionDeniedCode,
  'auth/operation-not-allowed',
  'auth/user-disabled',
]);
const NOT_FOUND_ERROR_CODES = new Set<string>([notFoundCode, 'auth/user-not-found']);
const CONFLICT_ERROR_CODES = new Set<string>([
  alreadyExistsCode,
  'auth/email-already-in-use',
  'auth/credential-already-in-use',
  'auth/account-exists-with-different-credential',
]);
const SERVER_ERROR_CODES = new Set<string>([resourceExhaustedCode, abortedCode, internalCode]);
const RETRYABLE_ERROR_CODES = new Set<string>([
  unavailableCode,
  deadlineExceededCode,
  resourceExhaustedCode,
  abortedCode,
  internalCode,
  'auth/network-request-failed',
  'ECONNABORTED',
  'ETIMEDOUT',
]);
